/**
 * Per-OHT simulation-time delay accumulated when an OHT is held somewhere the
 * shared timeline does not keep it: a circle junction, or a transfer pinned for
 * its full `holdWallSec`. Playback sampling uses `simT - delay`, so the vehicle
 * resumes from where it stood instead of jumping to wherever the timeline went.
 *
 * A delayed vehicle is reading its own past, so anything driven by the shared
 * clock — above all the replay snapshot's `activeTransfer` — describes a moment
 * it has not reached yet and must not be allowed to move it.
 */

type Listener = () => void;

class PlaybackDelayStore {
  private delaySecByOht = new Map<string, number>();
  private lastSettleRawT = new Map<string, number | null>();
  private listeners = new Set<Listener>();
  private version = 0;

  getDelaySec(ohtId: string): number {
    return this.delaySecByOht.get(ohtId) ?? 0;
  }

  /** Effective timeline time for an OHT (never negative). */
  getEffectiveTime(ohtId: string, simT: number): number {
    return Math.max(0, simT - this.getDelaySec(ohtId));
  }

  getVersion(): number {
    return this.version;
  }

  addDelay(ohtId: string, delaySec: number) {
    if (!(delaySec > 0) || !Number.isFinite(delaySec)) return;
    const next = this.getDelaySec(ohtId) + delaySec;
    this.delaySecByOht.set(ohtId, next);
    this.emit();
  }

  /**
   * Give back delay while this vehicle's own route has it standing still, so a
   * wait or junction dwell is where it rejoins the shared clock instead of
   * trailing it for the rest of the run. It is only ever repaid down to zero,
   * so the vehicle moves towards the backend timeline and never past it.
   *
   * Call once per rendered frame per vehicle. Repeated calls at the same
   * `rawSimTimeS` repay nothing, so the 2D and 3D overlays cannot double-repay.
   */
  settle(ohtId: string, rawSimTimeS: number, stationary: boolean): void {
    const previous = this.lastSettleRawT.get(ohtId);
    this.lastSettleRawT.set(ohtId, stationary ? rawSimTimeS : null);
    // Only a span that was parked at both ends was time the vehicle did not
    // need. Time it spent driving pays for the route it was driving.
    if (!stationary || previous == null) return;

    const elapsed = rawSimTimeS - previous;
    const delay = this.getDelaySec(ohtId);
    if (!(elapsed > 0) || delay <= 0) return;

    const next = Math.max(0, delay - elapsed);
    if (next === delay) return;
    if (next === 0) this.delaySecByOht.delete(ohtId);
    else this.delaySecByOht.set(ohtId, next);
    this.emit();
  }

  reset() {
    this.lastSettleRawT.clear();
    if (this.delaySecByOht.size === 0) return;
    this.delaySecByOht.clear();
    this.emit();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit() {
    this.version += 1;
    for (const listener of this.listeners) listener();
  }
}

export const playbackDelayStore = new PlaybackDelayStore();
